import React, { useMemo } from "react";
import { Card } from "antd";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line
} from "recharts";
import { UserOutlined, DollarOutlined, CalendarOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { formatDateTime } from "../utils/functions";
import Spinner from "./global/spinner";
import NoRecordFound from "./global/noRecordFound";
import CommonTable from "./global/table";
import { useGenericQuery } from "../queries/use-generic-query";
import { QUERY_KEYS } from "../lib/query-constants";
// import SOURCES from "../utils/constants";

// --- Constants ---
const CHART_COLORS = [
    "#2dbdf2", // Primary Blue
    "#3b3839", // Secondary Dark
    "#F59E0B", // Amber
    "#EC4899", // Pink
];

// --- Sub-components ---

const CustomTooltip = ({ active, payload, label, prefix = "", suffix = "" }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white border text-gray-800 p-3 rounded-xl shadow-xl text-sm">
                <p className="font-medium opacity-70 mb-1">{label}</p>
                <div className="flex items-center gap-2">
                    <span className="font-bold text-lg text-primary">
                        {prefix}{payload[0].value.toLocaleString()}{suffix}
                    </span>
                </div>
            </div>
        );
    }
    return null;
};

const SparkLine = ({ data, color }) => {
    // Generate mock dots if no data
    const safeData = Array.isArray(data) && data.length > 0 ? data : Array.from({ length: 7 }, () => ({ count: Math.floor(Math.random() * 50) + 20 }));

    return (
        <div className="h-12 w-28">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={safeData}>
                    <Line
                        type="monotone"
                        dataKey="count"
                        stroke={color}
                        strokeWidth={2}
                        dot={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

const KPICard = ({ title, value, icon, variant = "default", sparkData, trendValue }) => {
    const isPrimary = variant === "primary";

    return (
        <Card
            hoverable
            className={`h-full transition-all duration-300 ${isPrimary ? "!bg-primary shadow-lg shadow-blue-100" : "bg-white border-gray-100 shadow-sm"}`}
            styles={{ body: { padding: '24px', height: '100%' } }}
        >
            <div className="relative h-full flex flex-col justify-between">
                {/* Top Row: Icon + Trend */}
                <div className="flex items-center justify-between mb-4">
                    <div className={`
                        w-12 h-12 rounded-full flex items-center justify-center text-xl
                        ${isPrimary ? "bg-white/20 text-white" : "bg-primary/10 text-primary"}
                    `}>
                        {icon}
                    </div>
                    {trendValue && (
                        <div className={`px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${isPrimary ? "bg-white/20 text-white" : "!bg-primary text-white"
                            }`}>
                            <ArrowUpOutlined /> {trendValue}
                        </div>
                    )}
                </div>

                {/* Middle Row: Value */}
                <div className="mb-2 z-10 relative">
                    <p className={`text-sm font-medium ${isPrimary ? "text-blue-50" : "text-gray-400"}`}>{title}</p>
                    <h3 className={`text-3xl font-bold ${isPrimary ? "text-white" : "text-gray-800"}`}>
                        {value}
                    </h3>
                </div>

                {/* Sparkline Overlay */}
                <div className="absolute bottom-0 right-0 opacity-40 pointer-events-none">
                    <SparkLine
                        data={sparkData}
                        color={isPrimary ? "#fff" : "#2dbdf2"}
                    />
                </div>
            </div>
        </Card>
    );
};

const ReferralsBarChart = ({ data }) => {
    const chartData = useMemo(() => {
        if (!Array.isArray(data)) return [];
        // Group by date or just show raw if small
        return data.slice(0, 10).map((cd, index) => ({
            name: cd.User?.[0]?.name || `Ref ${index + 1}`,
            value: cd.commissionAmount || 0,
        }));
    }, [data]);

    if (chartData.length === 0) {
        return <NoRecordFound message="No referral data available." showRetry={false} className="h-[350px]" />;
    }

    return (
        <div className="w-full h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }} barSize={32}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                    <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#9CA3AF", fontSize: 12 }}
                        dy={10}
                    />
                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#9CA3AF", fontSize: 12 }}
                    />
                    <RechartsTooltip content={<CustomTooltip label="Customer" prefix="$" />} cursor={{ fill: "#E0F2FE" }} />
                    <Bar
                        dataKey="value"
                        fill="#2dbdf2"
                        radius={[8, 8, 0, 0]}
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

const ComparisonDonutChart = ({ total, currentMonth }) => {
    const chartData = [
        { name: "Total Referrals", value: total },
        { name: "Current Month", value: currentMonth },
    ];

    if (total === 0 && currentMonth === 0) {
        return <NoRecordFound message="No comparison data available." showRetry={false} className="h-[350px]" />;
    }

    return (
        <div className="w-full h-[350px] flex items-center justify-center relative">
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={110}
                        paddingAngle={10}
                        dataKey="value"
                        cornerRadius={10}
                    >
                        {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} strokeWidth={0} />
                        ))}
                    </Pie>
                    <RechartsTooltip
                        content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                                return (
                                    <div className="bg-white p-3 border border-gray-100 shadow-xl rounded-xl text-sm">
                                        <div className="font-bold text-gray-800 mb-1">{payload[0].name}</div>
                                        <div className="text-primary font-bold text-lg">
                                            {payload[0].value.toLocaleString()}
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        }}
                    />
                    <Legend
                        verticalAlign="bottom"
                        align="center"
                        iconType="circle"
                        wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                        formatter={(value) => <span className="text-gray-500 font-medium ml-1">{value}</span>}
                    />
                </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pb-12">
                <span className="text-4xl font-bold text-gray-800">
                    {total.toLocaleString()}
                </span>
                <span className="text-xs text-gray-400 font-medium uppercase tracking-widest">Grand Total</span>
            </div>
        </div>
    );
};

// --- Main Component ---

const EnhancedAffiliateDashboard = ({ currentUser }) => {
    const affiliateId = currentUser?.user?._id;

    // API Hooks
    const { data: monthReferrals, isLoading: leadsLoading } = useGenericQuery({
        endpoint: "admin/affiliatecommission/current-month-referrals",
        queryParams: { affiliateId },
        queryKey: QUERY_KEYS.affiliates.lists({ type: "current-month", affiliateId }),
        enabled: !!affiliateId
    });

    const { data: allCommissions, isLoading: commissionsLoading } = useGenericQuery({
        endpoint: "admin/affiliatecommission/",
        queryParams: { affiliateId },
        queryKey: QUERY_KEYS.affiliates.lists({ type: "all", affiliateId }),
        enabled: !!affiliateId
    });

    // Derived Data
    const totalCommission = useMemo(() => {
        if (!allCommissions?.results) return 0;
        return allCommissions.results.reduce((acc, curr) => acc + (curr.commissionAmount || 0), 0);
    }, [allCommissions]);

    const totalCustomers = allCommissions?.totalRecords || 0;
    const currentMonthCount = Array.isArray(monthReferrals) ? monthReferrals.length : 0;

    // Table Columns
    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            isAvatar: true
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
        },
        {
            title: "Registered At",
            dataIndex: "createdAt",
            key: "createdAt",
        },
        {
            title: "Plan / Price",
            dataIndex: "planDetails",
            key: "planDetails",
        },
        {
            title: "Commission",
            dataIndex: "commission",
            key: "commission",
            render: (text) => <span className="font-bold text-primary">{text}</span>
        }
    ];

    // Table Data Mapping
    const tableData = useMemo(() => {
        if (!allCommissions?.results) return [];
        return allCommissions.results.flatMap((item) => {
            return (item.User || []).map((u) => ({
                key: `${item._id}-${u._id}`,
                name: u.name,
                email: u.email,
                createdAt: formatDateTime(u.createdAt),
                planDetails: (item.Plan || []).map(p => `${p.planName} / ${p.planPrice}`).join(", "),
                commission: `${item.commissionAmount} $`,
            }));
        });
    }, [allCommissions]);

    return (
        <div className="min-h-screen bg-gray-50/50 p-8 space-y-10 animate-in fade-in zoom-in duration-500">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">Affiliate Overview</h1>
                    <p className="text-gray-500 mt-2 font-medium">Welcome back, {currentUser?.user?.name || 'Partner'} 👋</p>
                </div>
                <div className="hidden md:block">
                    <div className="bg-white px-6 py-3 rounded-full shadow-sm border border-gray-100 text-sm font-semibold text-gray-600 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full !bg-primary animate-pulse"></span>
                        {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </div>
                </div>
            </div>

            {/* KPI Cards */}
            {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <KPICard
                    title="Total Customers"
                    value={totalCustomers.toLocaleString()}
                    icon={<UserOutlined />}
                    variant="primary"
                />
                <KPICard
                    title="Month Referrals"
                    value={currentMonthCount.toLocaleString()}
                    icon={<CalendarOutlined />}
                    sparkData={monthReferrals?.map(r => ({ count: r.commissionAmount }))}
                />
                <KPICard
                    title="Total Commission"
                    value={`$${totalCommission.toLocaleString()}`}
                    icon={<DollarOutlined />}
                />
            </div> */}

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 ring-1 ring-black/5 hover:ring-primary/20 transition-all duration-300">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-800">Referral Commissions</h3>
                            <p className="text-gray-400 text-sm">Earnings from recent customers</p>
                        </div>
                        <div className="bg-primary/10 px-4 py-2 rounded-xl text-xs font-bold text-primary uppercase tracking-wider">
                            Real-time
                        </div>
                    </div>
                    {leadsLoading ? <div className="h-[350px] flex items-center justify-center"><Spinner className="w-8 h-8" /></div> : <ReferralsBarChart data={monthReferrals} />}
                </div>

                <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 ring-1 ring-black/5 hover:ring-primary/20 transition-all duration-300">
                    <h3 className="text-xl font-bold text-gray-800 mb-6 font-primary">Referral Comparison</h3>
                    {commissionsLoading || leadsLoading ? (
                        <div className="h-[350px] flex items-center justify-center"><Spinner className="w-8 h-8" /></div>
                    ) : (
                        <ComparisonDonutChart total={totalCustomers} currentMonth={currentMonthCount} />
                    )}
                </div>
            </div>

            {/* Customer Table */}
            <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 ring-1 ring-black/5">
                <div className="mb-8">
                    <h3 className="text-2xl font-bold text-gray-800">Your Customers</h3>
                    <p className="text-gray-400 text-sm">Detailed list of all referred users and plans</p>
                </div>
                {commissionsLoading ? (
                    <div className="h-[400px] flex items-center justify-center"><Spinner /></div>
                ) : (
                    <CommonTable
                        columns={columns}
                        data={tableData}
                        pagination={{ pageSize: 10 }}
                    />
                )}
            </div>
        </div>
    );
};

export default EnhancedAffiliateDashboard;
